<template>
  <v-text-field
    :value="displayValue"
    :placeholder="placeholder"
    :label="label"
    :hint="hint"
    :persistent-hint="persistentHint"
    :outlined="outlined"
    :filled="filled"
    :solo="solo"
    :dense="dense"
    :rounded="rounded"
    :disabled="disabled"
    :readonly="readonly"
    :clearable="clearable"
    :prepend-icon="prependIcon"
    :append-icon="appendIcon"
    :prepend-inner-icon="prependInnerIcon"
    :append-outer-icon="appendOuterIcon"
    :color="color"
    :background-color="backgroundColor"
    :class="inputClass"
    :style="inputStyle"
    :rules="combinedRules"
    :error="error"
    :error-messages="errorMessages"
    :success="success"
    :success-messages="successMessages"
    :messages="messages"
    :counter="counter"
    :maxlength="maxlength"
    :prefix="displayPrefix"
    :suffix="displaySuffix"
    v-bind="$attrs"
    @input="handleInput"
    @blur="handleBlur"
    @focus="handleFocus"
    @keypress="handleKeypress"
    @click="$emit('click', $event)"
  />
</template>

<script>
export default {
  name: 'CurrencyInput',
  
  model: {
    prop: 'value',
    event: 'input'
  },
  
  props: {
    // v-model value
    value: {
      type: [Number, String],
      default: null
    },
    
    // Formato de moneda
    currency: {
      type: String,
      default: 'CLP' // CLP, USD, EUR, etc.
    },
    
    locale: {
      type: String,
      default: 'es-CL' // es-CL, en-US, etc.
    },
    
    precision: {
      type: Number,
      default: 2,
      validator: val => val >= 0 && val <= 10
    },
    
    // Prefijo y sufijo personalizados
    prefix: {
      type: String,
      default: ''
    },
    
    suffix: {
      type: String,
      default: ''
    },
    
    // Prefijo visual (para el mask, no afecta el valor)
    maskPrefix: {
      type: String,
      default: ''
    },
    
    // Sufijo visual (para el mask, no afecta el valor)
    maskSuffix: {
      type: String,
      default: ''
    },
    
    // Validaciones
    min: {
      type: Number,
      default: null
    },
    
    max: {
      type: Number,
      default: null
    },
    
    required: {
      type: Boolean,
      default: false
    },
    
    // Props de Vuetify v-text-field
    placeholder: String,
    label: String,
    hint: String,
    persistentHint: Boolean,
    outlined: Boolean,
    filled: Boolean,
    solo: Boolean,
    dense: Boolean,
    rounded: Boolean,
    disabled: Boolean,
    readonly: Boolean,
    clearable: Boolean,
    prependIcon: String,
    appendIcon: String,
    prependInnerIcon: String,
    appendOuterIcon: String,
    color: String,
    backgroundColor: String,
    inputClass: [String, Array, Object],
    inputStyle: [String, Array, Object],
    rules: {
      type: Array,
      default: () => []
    },
    error: Boolean,
    errorMessages: [String, Array],
    success: Boolean,
    successMessages: [String, Array],
    messages: [String, Array],
    counter: [Boolean, Number, String],
    maxlength: [Number, String]
  },
  
  data() {
    return {
      isFocused: false,
      rawInput: ''
    }
  },
  
  computed: {
    displayValue: {
      get() {
        // Aplicar prefijo y sufijo del mask si existen
        let value = this.rawInput || this.formattedValue;
        
        // Solo agregar mask prefix/suffix si hay valor
        if (value && (this.maskPrefix || this.maskSuffix)) {
          value = (this.maskPrefix || '') + value + (this.maskSuffix || '');
        }
        
        return value;
      },
      set(value) {
        // Remover mask prefix/suffix antes de procesar
        let cleanValue = value;
        
        if (this.maskPrefix && cleanValue.startsWith(this.maskPrefix)) {
          cleanValue = cleanValue.substring(this.maskPrefix.length);
        }
        
        if (this.maskSuffix && cleanValue.endsWith(this.maskSuffix)) {
          cleanValue = cleanValue.substring(0, cleanValue.length - this.maskSuffix.length);
        }
        
        this.rawInput = cleanValue;
      }
    },
    
    formattedValue() {
      if (this.value === null || this.value === undefined || this.value === '') {
        return '';
      }
      
      try {
        let formatted;
        if (this.currency === 'none') {
          formatted = new Intl.NumberFormat(this.locale, {
            minimumFractionDigits: 0,
            maximumFractionDigits: this.precision
          }).format(this.value);
        } else {
          formatted = new Intl.NumberFormat(this.locale, {
            style: 'currency',
            currency: this.currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: this.precision
          }).format(this.value);
        }
        
        // Si hay maskPrefix o maskSuffix, remover el símbolo de moneda para evitar duplicados
        if ((this.maskPrefix || this.maskSuffix) && this.currency !== 'none') {
          // Remover símbolos de moneda del formato Intl
          formatted = formatted.replace(/[$€£¥₹₩¢\s]/g, '').trim();
        }
        
        return formatted;
      } catch (error) {
        return this.value.toString();
      }
    },
    
    displayPrefix() {
      return this.prefix || null;
    },
    
    displaySuffix() {
      return this.suffix || null;
    },
    
    combinedRules() {
      const rules = [...this.rules];
      
      if (this.required) {
        rules.push(v => {
          return (v !== null && v !== undefined && v !== '') || 'Este campo es requerido';
        });
      }
      
      if (this.min !== null) {
        rules.push(v => {
          if (v === null || v === undefined || v === '') return true;
          return v >= this.min || `El valor debe ser mayor o igual a ${this.min}`;
        });
      }
      
      if (this.max !== null) {
        rules.push(v => {
          if (v === null || v === undefined || v === '') return true;
          return v <= this.max || `El valor debe ser menor o igual a ${this.max}`;
        });
      }
      
      // Validación de número válido mejorada
      rules.push(v => {
        if (v === null || v === undefined || v === '') return true;
        
        // Si es número, es válido
        if (typeof v === 'number' && !isNaN(v)) return true;
        
        // Si es string, verificar que sea un formato numérico válido
        if (typeof v === 'string') {
          const str = v.replace(/[^\d,.-]/g, '');
          const forValidation = str.replace(/\./g, '').replace(',', '.');
          return !isNaN(parseFloat(forValidation)) || 'Debe ser un número válido';
        }
        
        return 'Debe ser un número válido';
      });
      
      return rules;
    }
  },
  
  methods: {
    handleInput(inputValue) {
      // Remover mask prefix/suffix del input antes de procesar
      let cleanInputValue = inputValue;
      
      if (this.maskPrefix && cleanInputValue.startsWith(this.maskPrefix)) {
        cleanInputValue = cleanInputValue.substring(this.maskPrefix.length);
      }
      
      if (this.maskSuffix && cleanInputValue.endsWith(this.maskSuffix)) {
        cleanInputValue = cleanInputValue.substring(0, cleanInputValue.length - this.maskSuffix.length);
      }
      
      if (!cleanInputValue || cleanInputValue === '') {
        this.rawInput = '';
        this.$emit('input', null);
        this.$emit('change', null);
        return;
      }
      
      // Limpiar: solo números, coma y punto
      const cleaned = cleanInputValue.replace(/[^\d,.-]/g, '');
      
      // Separar por coma (decimal)
      const commaParts = cleaned.split(',');
      let integerPart = commaParts[0] || '';
      const decimalPart = commaParts[1] || '';
      
      // Limpiar parte entera: remover TODOS los puntos primero
      const cleanInteger = integerPart.replace(/\./g, '');
      
      // Crear valor numérico para validar
      let valueForNumber = cleanInteger;
      if (commaParts.length > 1) {
        valueForNumber += '.' + decimalPart;
      }
      
      const numericValue = parseFloat(valueForNumber);
      
      // Validar que sea un número válido
      if (isNaN(numericValue) && valueForNumber !== '') {
        return; // No actualizar si no es válido
      }
      
      // Formatear parte entera con separadores
      let formattedInteger = '';
      if (cleanInteger) {
        // Usar Intl para formatear, luego extraer solo la parte numérica
        try {
          const tempNumber = parseInt(cleanInteger);
          if (!isNaN(tempNumber)) {
            formattedInteger = new Intl.NumberFormat('es-CL').format(tempNumber);
            // Remover cualquier símbolo que no sea número o punto
            formattedInteger = formattedInteger.replace(/[^\d.]/g, '');
          }
        } catch (e) {
          formattedInteger = cleanInteger;
        }
      }
      
      // Construir valor para display (SIN mask prefix/suffix)
      let displayValue = formattedInteger;
      if (commaParts.length > 1) {
        displayValue += ',' + decimalPart;
      }
      
      // Actualizar el input visual (el displayValue agregará el mask automáticamente)
      this.rawInput = displayValue;
      
      // Emitir el valor numérico
      if (!isNaN(numericValue)) {
        this.$emit('input', numericValue);
        this.$emit('change', numericValue);
      } else if (valueForNumber === '') {
        this.$emit('input', null);
        this.$emit('change', null);
      }
    },
    
    handleFocus(event) {
      this.isFocused = true;
      // Preparar valor para edición
      if (this.value !== null && this.value !== undefined) {
        // Formatear el valor inicial con separadores mientras escribe
        const valueStr = this.value.toString();
        const parts = valueStr.split('.');
        const integerPart = new Intl.NumberFormat('es-CL').format(parseInt(parts[0]) || 0).replace(/[^\d.]/g, '');
        const decimalPart = parts[1] || '';
        
        this.rawInput = decimalPart ? integerPart + ',' + decimalPart : integerPart;
      } else {
        this.rawInput = '';
      }
      this.$emit('focus', event);
    },
    
    handleBlur(event) {
      this.isFocused = false;
      this.$emit('blur', event);
    },
    
    handleKeypress(event) {
      // Permitir solo números, coma, punto y teclas de control
      const allowedKeys = /[\d,.-]/;
      const controlKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight'];
      
      if (!controlKeys.includes(event.key) && !allowedKeys.test(event.key)) {
        event.preventDefault();
      }
      
      // Permitir solo una coma decimal
      if (event.key === ',' && this.rawInput.includes(',')) {
        event.preventDefault();
      }
      
      this.$emit('keypress', event);
    }
  },
  
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (!this.isFocused) {
          // Cuando el valor cambia desde fuera y no está enfocado
          if (newVal !== null && newVal !== undefined) {
            const valueStr = newVal.toString();
            const parts = valueStr.split('.');
            const integerPart = new Intl.NumberFormat('es-CL').format(parseInt(parts[0]) || 0).replace(/[^\d.]/g, '');
            const decimalPart = parts[1] || '';
            
            this.rawInput = decimalPart ? integerPart + ',' + decimalPart : integerPart;
          } else {
            this.rawInput = '';
          }
        }
      }
    }
  }
}
</script>